// @lat: [[action-items#Action Items]]
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ListChecks, ShieldCheck, UploadCloud, ScanSearch, NotebookPen,
  RefreshCw, AlertTriangle, Lightbulb, X, CheckCircle2, ArrowRight, Plus,
} from "lucide-react";
import * as api from "../../lib/api";
import { PRIMARY } from "./shared";

const PRIORITY_STYLES = {
  critical: { bg: "#FFF1F2", text: "#BE123C", label: "Critical" },
  high:     { bg: "#FFF7ED", text: "#C2410C", label: "High" },
  medium:   { bg: "#FFFBEB", text: "#B45309", label: "Medium" },
  low:      { bg: "#F4F4F5", text: "#52525B", label: "Low" },
};

const TYPE_ICONS = {
  audit: ShieldCheck,
  indexing: UploadCloud,
  query_gap: ScanSearch,
  content_cadence: NotebookPen,
  content_freshness: RefreshCw,
  cannibalization: AlertTriangle,
  recommendations: Lightbulb,
};

function ItemCard({ item, onDismiss, onAccept, busy, canEdit, canCreate }) {
  const Icon = TYPE_ICONS[item.type] || ListChecks;
  const p = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.low;
  return (
    <div className="flex items-start gap-4 bg-white rounded-xl border border-zinc-100 shadow-sm px-5 py-4">
      <div
        className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: p.bg }}
      >
        <Icon size={18} style={{ color: p.text }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
            style={{ backgroundColor: p.bg, color: p.text }}
          >
            {p.label}
          </span>
        </div>
        <p className="text-sm font-bold text-zinc-900">{item.title}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{item.reason}</p>
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        <Link
          to={item.cta_link}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
        >
          {item.cta_label}
          <ArrowRight size={12} />
        </Link>
        {canCreate && (
          <button
            onClick={() => onAccept(item.key)}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg text-white transition-colors disabled:opacity-40"
            style={{ backgroundColor: PRIMARY }}
          >
            <Plus size={12} /> Create task
          </button>
        )}
        {canEdit && (
          <button
            onClick={() => onDismiss(item.key)}
            disabled={busy}
            title="Dismiss"
            className="p-1.5 rounded-lg text-zinc-300 hover:text-zinc-500 hover:bg-zinc-50 transition-colors disabled:opacity-40"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// Computed suggestions (audit, indexing, query gaps, cadence, cannibalization,
// recommendations). "Create task" turns one into a task and hides it.
export default function SuggestedTab({ canEdit, canCreate, onAccepted, onCount }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [lastDismissed, setLastDismissed] = useState(null);

  useEffect(() => {
    api.getActionItems()
      .then(d => setItems(d.items || []))
      .catch(e => setError(e?.response?.data?.detail || "Failed to load suggestions."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (items) onCount?.(items.length); }, [items, onCount]);

  const handleDismiss = async (key) => {
    setBusyKey(key);
    const dismissed = items.find(i => i.key === key);
    try {
      await api.dismissActionItem(key);
      setItems(prev => prev.filter(i => i.key !== key));
      setLastDismissed(dismissed || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to dismiss item.");
    } finally {
      setBusyKey(null);
    }
  };

  const handleAccept = async (key) => {
    setBusyKey(key);
    try {
      const task = await api.acceptActionItem(key);
      setItems(prev => prev.filter(i => i.key !== key));
      onAccepted?.(task);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create task.");
    } finally {
      setBusyKey(null);
    }
  };

  const handleUndo = async () => {
    if (!lastDismissed) return;
    try {
      await api.restoreActionItem(lastDismissed.key);
      setItems(prev => [...prev, lastDismissed].sort(
        (a, b) => Object.keys(PRIORITY_STYLES).indexOf(a.priority) - Object.keys(PRIORITY_STYLES).indexOf(b.priority)
      ));
    } finally {
      setLastDismissed(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-zinc-400 text-sm">Loading…</div>;

  return (
    <>
      {error && <div className="mb-3 px-4 py-2.5 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">{error}</div>}
      {items && items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <CheckCircle2 size={40} className="text-green-500" />
          <p className="text-zinc-700 text-sm font-bold">You're all caught up.</p>
          <p className="text-zinc-400 text-xs text-center max-w-xs">
            No open issues, indexing gaps, query gaps, or overdue content right now.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {(items || []).map(item => (
            <ItemCard
              key={item.key}
              item={item}
              canEdit={canEdit}
              canCreate={canCreate}
              onDismiss={handleDismiss}
              onAccept={handleAccept}
              busy={busyKey === item.key}
            />
          ))}
        </div>
      )}

      {lastDismissed && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-xs font-medium rounded-lg px-4 py-2.5 flex items-center gap-3 shadow-lg">
          Dismissed "{lastDismissed.title}"
          <button onClick={handleUndo} className="font-bold underline" style={{ color: "#4ADE80" }}>
            Undo
          </button>
        </div>
      )}
    </>
  );
}
